import React from 'react';
import { Baby, User, Users as UsersIcon, Video, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const Services = () => {
  const services = [
    {
      icon: Baby,
      title: 'Çocuk-Ergen Psikolojik Danışmanlık',
      description: 'Çocukluk ve ergenlik döneminde yaşanan duygusal, davranışsal ve gelişimsel zorluklara profesyonel destek.',
      image: 'https://images.unsplash.com/photo-1541976844346-f18aeac57b06?w=600&q=80',
      benefits: [
        'Davranış problemleri',
        'Okul başarısızlığı',
        'Sosyal ilişki zorlukları',
        'Dikkat ve konsantrasyon',
        'Anksiyete ve korku yönetimi',
        'Aile içi sorunlar'
      ],
      color: 'from-sage-green to-mint-green'
    },
    {
      icon: User,
      title: 'Bireysel Psikolojik Danışmanlık',
      description: 'Kişisel gelişim, duygusal zorluklar ve yaşam sorunları için bire bir psikolojik destek.',
      image: 'https://images.pexels.com/photos/6958258/pexels-photo-6958258.jpeg?w=600&q=80',
      benefits: [
        'Depresyon ve anksiyete',
        'Stres yönetimi',
        'Özgüven geliştirme',
        'Travma ve kayıp',
        'Kişisel gelişim',
        'Yaşam dengesi'
      ],
      color: 'from-forest-green to-sage-green'
    },
    {
      icon: UsersIcon,
      title: 'Çift-Evlilik ve Aile Psikolojik Danışmanlık',
      description: 'Çiftler ve aileler için ilişki sorunlarının çözümüne yönelik profesyonel danışmanlık.',
      image: 'https://images.unsplash.com/flagged/photo-1569744068983-6dfc2f27deb8?w=600&q=80',
      benefits: [
        'İletişim sorunları',
        'Çatışma yönetimi',
        'Güven problemleri',
        'Boşanma süreci',
        'Aile içi dinamikler',
        'İlişki güçlendirme'
      ],
      color: 'from-mint-green to-light-sage'
    },
    {
      icon: Video,
      title: 'Online Psikolojik Danışmanlık',
      description: 'Nerede olursanız olun, online platformlar üzerinden güvenli ve etkili psikolojik destek.',
      image: 'https://images.unsplash.com/photo-1562751362-404243c2eea3?w=600&q=80',
      benefits: [
        'Esnek randevu saatleri',
        'Evinizin konforunda',
        'Coğrafi kısıt yok',
        'Güvenli platform',
        'Yüz yüze kadar etkili',
        'Gizlilik garantisi'
      ],
      color: 'from-sage-green to-forest-green'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'İlk Görüşme',
      description: 'Size uygun bir randevu alın ve ilk görüşmede durumunuzu paylaşın.'
    },
    {
      step: '02',
      title: 'Değerlendirme',
      description: 'Uzman psikoloğunuz, ihtiyaçlarınızı değerlendirerek size özel bir plan oluşturur.'
    },
    {
      step: '03',
      title: 'Terapi Süreci',
      description: 'Düzenli seansınızda hedeflerinize ulaşmak için birlikte çalışırız.'
    },
    {
      step: '04',
      title: 'İzleme',
      description: 'Sürekliliği sağlamak ve gelişiminizi takip etmek için düzensiz seansınıza devam ederiz.'
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-beige-primary via-beige-secondary to-beige-primary overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-sage-green/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-mint-green/5 rounded-full blur-3xl"></div>
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-sage-green/10 rounded-full mb-6">
              <span className="text-sage-green font-medium text-sm">Hizmetlerimiz</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-forest-green mb-6">
              Size Özel Psikolojik Destek
            </h1>
            <p className="text-xl text-text-light leading-relaxed">
              Her yaş grubundan ve her ihtiyaçtan bireylere, çiftlere ve ailelere profesyonel psikolojik danışmanlık hizmetleri sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="space-y-20">
            {services.map((service, index) => {
              const Icon = service.icon;
              const isEven = index % 2 === 0;
              
              return (
                <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${
                  isEven ? '' : 'lg:grid-flow-dense'
                }`}>
                  <div className={isEven ? '' : 'lg:col-start-2'}>
                    <div className="relative">
                      <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl group">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-20`}></div>
                      </div>
                      <div className={`absolute -bottom-6 ${
                        isEven ? '-right-6' : '-left-6'
                      } w-48 h-48 bg-gradient-to-br ${service.color} opacity-10 rounded-3xl -z-0`}></div>
                    </div>
                  </div>

                  <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} mb-6`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-3xl font-bold text-forest-green mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-text-light mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="space-y-3 mb-8">
                      <p className="font-semibold text-forest-green">Yardımcı Olduğumuz Konular:</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {service.benefits.map((benefit, idx) => (
                          <div key={idx} className="flex items-start space-x-2">
                            <CheckCircle2 className="w-5 h-5 text-sage-green mt-0.5 flex-shrink-0" />
                            <span className="text-text-dark text-sm">{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Link to="/contact">
                      <Button className={`bg-gradient-to-r ${service.color} text-white hover:shadow-lg button-hover`}>
                        Randevu Alın
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="section-padding bg-gradient-to-br from-beige-primary to-beige-secondary">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-forest-green mb-4">Süreci Nasıl İşliyoruz?</h2>
            <p className="text-lg text-text-light max-w-2xl mx-auto">
              Psikolojik danışmanlık sürecinde adım adım neler olacağını öğrenin.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <Card key={index} className="p-8 border-none shadow-lg bg-white relative overflow-hidden group hover:shadow-xl transition-shadow duration-300">
                <div className="absolute top-0 right-0 text-[120px] font-bold text-sage-green/5 leading-none group-hover:text-sage-green/10 transition-colors duration-300">
                  {item.step}
                </div>
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sage-green to-mint-green flex items-center justify-center mb-6">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-forest-green mb-3">{item.title}</h3>
                  <p className="text-text-light leading-relaxed">{item.description}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container">
          <Card className="relative overflow-hidden border-none shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-forest-green to-sage-green"></div>
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://images.unsplash.com/photo-1573497491208-6b1acb260507?w=1200&q=80"
                alt="Background"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="relative z-10 p-12 md:p-16 text-center">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Size Uygun Hizmeti Bulmak İçin Buradayız
              </h2>
              <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
                Hangi hizmetin size uygun olduğundan emin değil misiniz? Uzmanlarımız size yardımcı olmak için hazır.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-sage-green hover:bg-beige-primary button-hover text-base px-8 py-6">
                    İletişime Geçin
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="tel:+905305128090">
                  <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-sage-green button-hover text-base px-8 py-6">
                    Hemen Ara
                  </Button>
                </a>
              </div>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
};